/**
 * YUMIX — Feature Discovery context.
 *
 * Tracks which onboarding hints the current user has seen so each tip
 * appears at most once per device. Uses localStorage for persistence
 * (zero backend round-trip) with a version prefix so bumping `TIPS_VERSION`
 * makes ALL users (including admin + existing clients) see the set again.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'yumix_seen_tips_v1';

const FeatureDiscoveryContext = createContext({
  hasSeen: () => true,
  markSeen: () => {},
  resetAll: () => {},
});

export const FeatureDiscoveryProvider = ({ children }) => {
  const [seen, setSeen] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...seen]));
    } catch { /* quota / safari private mode — tolerable */ }
  }, [seen]);

  const hasSeen = useCallback((id) => seen.has(id), [seen]);

  const markSeen = useCallback((id) => {
    setSeen((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    setSeen(new Set());
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  }, []);

  const value = useMemo(() => ({ hasSeen, markSeen, resetAll }), [hasSeen, markSeen, resetAll]);

  return (
    <FeatureDiscoveryContext.Provider value={value}>
      {children}
    </FeatureDiscoveryContext.Provider>
  );
};

export const useFeatureDiscovery = () => useContext(FeatureDiscoveryContext);
